export type CategoryId = 'recyclage' | 'dechets' | 'organiques' | 'jardin' | 'lourds' | 'speciales'

export interface Category {
  id: CategoryId
  label: string
  dot: string
  chip: string
  time: string
}

/** Les bacs et les libellés suivent ceux de Beaconsfield : bac bleu, bac gris, et les
    matières organiques en sacs de papier (le plastique est refusé, même biodégradable). */
export const categories: Record<CategoryId, Category> = {
  recyclage: {
    id: 'recyclage',
    label: 'Recyclage — bac bleu',
    dot: 'bg-[#2f5bc9]',
    chip: 'bg-[#e8edfb] text-[#1d3f8f]',
    time: '7 h',
  },
  dechets: {
    id: 'dechets',
    label: 'Ordures — bac gris',
    dot: 'bg-[#475569]',
    chip: 'bg-slate-100 text-slate-700',
    time: '7 h',
  },
  organiques: {
    id: 'organiques',
    label: 'Matières organiques',
    dot: 'bg-[#a16207]',
    chip: 'bg-amber-50 text-amber-800',
    time: '7 h',
  },
  jardin: {
    id: 'jardin',
    label: 'Branches et résidus verts',
    dot: 'bg-[#0f766e]',
    chip: 'bg-teal-50 text-teal-800',
    time: '7 h',
  },
  lourds: {
    id: 'lourds',
    label: 'Encombrants et CRD',
    dot: 'bg-[#7c3aed]',
    chip: 'bg-violet-50 text-violet-800',
    time: '7 h',
  },
  speciales: {
    id: 'speciales',
    label: 'Collectes spéciales',
    dot: 'bg-[#db2777]',
    chip: 'bg-pink-50 text-pink-800',
    time: '7 h',
  },
}

/**
 * Calendrier du secteur SUD de l'autoroute 20, où se trouve l'adresse au profil.
 *
 * Rythme réel de Beaconsfield : ordures et recyclage le jeudi pour toute la ville,
 * matières organiques le mardi au sud (le lundi au nord), encombrants et CRD le premier
 * jeudi du mois d'avril à novembre, et deux collectes de branches à l'automne.
 * « Aujourd'hui » = mardi 6 octobre 2026.
 */
export const collectionsByDate: Record<string, CategoryId[]> = {
  '2026-09-01': ['organiques'],
  '2026-09-03': ['dechets', 'recyclage', 'lourds'],
  '2026-09-08': ['organiques'],
  '2026-09-10': ['dechets', 'recyclage'],
  '2026-09-15': ['organiques', 'jardin'],
  '2026-09-17': ['dechets', 'recyclage'],
  '2026-09-22': ['organiques'],
  '2026-09-24': ['dechets', 'recyclage'],
  '2026-09-29': ['organiques'],
  '2026-10-01': ['dechets', 'recyclage', 'lourds'],
  '2026-10-06': ['organiques'],
  '2026-10-08': ['dechets', 'recyclage'],
  '2026-10-13': ['organiques', 'jardin'],
  '2026-10-15': ['dechets', 'recyclage'],
  '2026-10-20': ['organiques'],
  '2026-10-22': ['dechets', 'recyclage'],
  '2026-10-27': ['organiques'],
  '2026-10-29': ['dechets', 'recyclage'],
  '2026-11-03': ['organiques'],
  '2026-11-05': ['dechets', 'recyclage', 'lourds'],
}

export const monthNames = [
  'JANVIER',
  'FÉVRIER',
  'MARS',
  'AVRIL',
  'MAI',
  'JUIN',
  'JUILLET',
  'AOÛT',
  'SEPTEMBRE',
  'OCTOBRE',
  'NOVEMBRE',
  'DÉCEMBRE',
]

export const weekdayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']

export const longWeekdayNames = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi']

export const longMonthNames = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
]

export function toKey(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

export function formatLongDate(date: Date): string {
  const weekday = longWeekdayNames[date.getDay()]
  return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)} ${date.getDate()} ${longMonthNames[date.getMonth()]}`
}
